import { getConnect as getConnect$1, login as login$1, connect, disconnect, subscribe as subscribe$1, unsubscribe as unsubscribe$1, send, ConnState, onConnected } from '@monterosa-sdk/connect-kit';
import { Emitter, memoizePromise, subscribe as subscribe$2, onTick, now, checkAvailability, getItem, setItem, createError } from '@monterosa-sdk/util';
import { getSdk } from '@monterosa-sdk/core';

/**
 * @license
 * api.ts
 * interact-kit
 *
 * Created by Rygor Kharytanovich <rygor@monterosa.co.uk> on 2023-05-24
 * Copyright © 2023 Monterosa. All rights reserved.
 *
 * More details on the license can be found at https://www.monterosa.co/sdk/license
 */
/**
 * Sign in the user to submit data that requires a verified user.
 *
 * @example
 * ```javascript
 * try {
 *   const sdk = getSdk();
 *   const userId = "b236e331-ee62-4238-90d0-47588c7facdc";
 *   const timestamp = 1684934960;
 *   const signature = "9ABC4892838DC13986A63B736E95A7D99DF95C72";
 *
 *   await login(sdk, userId, timestamp, signature);
 *
 *   console.log('logged in successfully!');
 * } catch (err) {
 *   console.log(err)
 * }
 * ```
 *
 * @param sdk - Monterosa SDK instance
 * @param userId - The unique user UUID
 * @param timestamp - The timecode when the signature was generated
 * @param signature - A unique identifier that serves as proof of the user's authenticity.
 *  The signature must only contain the following characters: 0-9, a-z, A-Z, and must be uppercased
 * @returns A Promise that resolves with `void` when the login is successful, otherwise it rejects.
 */
async function login(sdk, userId, timestamp, signature) {
    const connect = await getConnect$1(sdk.options.host);
    return login$1(connect, userId, timestamp, signature);
}

/**
 * @license
 * public-types.ts
 * interact-kit
 *
 * Created by Rygor Kharytanovich <rygor@monterosa.co.uk> on 2022-11-24
 * Copyright © 2022 Monterosa. All rights reserved.
 *
 * More details on the license can be found at https://www.monterosa.co/sdk/license
 */
/**
 * @internal
 */
var State;
(function (State) {
    State["Ok"] = "ok";
    State["Error"] = "error";
})(State || (State = {}));
/**
 * @internal
 */
var Klass;
(function (Klass) {
    Klass["Listings"] = "listings";
    Klass["History"] = "history";
    Klass["Create"] = "create";
    Klass["Revoke"] = "revoke";
    Klass["Stop"] = "stop";
    Klass["Reveal"] = "reveal";
    Klass["Poll"] = "p";
    Klass["Feedback"] = "fb";
    Klass["Eoc"] = "eoc";
    Klass["Vote"] = "v";
})(Klass || (Klass = {}));
/**
 * @internal
 */
var Channel;
(function (Channel) {
    Channel["Poll"] = "p";
    Channel["Live"] = "l";
})(Channel || (Channel = {}));

/**
 * @license
 * connect_impl.ts
 * interact-kit
 *
 * Created by Rygor Kharytanovich <rygor@monterosa.co.uk> on 2022-11-24
 * Copyright © 2022 Monterosa. All rights reserved.
 *
 * More details on the license can be found at https://www.monterosa.co/sdk/license
 */
class ConnectImpl extends Emitter {
    constructor(host) {
        super();
        this.host = host;
        this._state = State.Ok;
        this._enmasse = null;
    }
    get state() {
        return this._state;
    }
    set state(state) {
        if (state === this._state) {
            return;
        }
        this._state = state;
        this.emit('state', this._state);
    }
    get enmasse() {
        if (this._enmasse === null) {
            throw new Error('Connect is not initialised, init() method should be called first.');
        }
        return this._enmasse;
    }
    set enmasse(enmasse) {
        this._enmasse = enmasse;
    }
    async init() {
        this.enmasse = await getConnect$1(this.host);
        this.enmasse.on('message', this.handleMessage.bind(this));
        this.enmasse.on('state', this.handleState.bind(this));
    }
    async connect() {
        await connect(this.enmasse);
    }
    disconnect() {
        disconnect(this.enmasse);
    }
    async subscribeProject(id) {
        await subscribe$1(this.enmasse, `${Channel.Live}-${id}`);
    }
    unsubscribeProject(id) {
        unsubscribe$1(this.enmasse, `${Channel.Live}-${id}`);
    }
    async subscribeEvent(id) {
        await Promise.all([
            subscribe$1(this.enmasse, `${Channel.Live}-${id}`),
            subscribe$1(this.enmasse, `${Channel.Poll}-${id}`),
        ]);
    }
    unsubscribeEvent(id) {
        unsubscribe$1(this.enmasse, `${Channel.Live}-${id}`);
        unsubscribe$1(this.enmasse, `${Channel.Poll}-${id}`);
    }
    sendVote(eventId, pollId, answer) {
        send(this.enmasse, `${Channel.Poll}-${eventId}`, Klass.Vote, [
            pollId,
            answer,
            '',
        ]);
    }
    handleMessage(message) {
        const { klass, body: data, channel } = message;
        const id = channel.substring(channel.indexOf('-') + 1);
        this.emit('message', {
            id,
            klass,
            data,
        });
    }
    handleState(state, ...args) {
        switch (state) {
            case ConnState.Disconnected:
                this.state = State.Error;
                break;
            case ConnState.Connecting: {
                const [attempt] = args;
                if (attempt > 1) {
                    this.state = State.Error;
                }
                break;
            }
            case ConnState.Connected:
                this.state = State.Ok;
                break;
        }
    }
}

/**
 * @license
 * api.ts
 * interact-kit
 *
 * Created by Rygor Kharytanovich <rygor@monterosa.co.uk> on 2022-11-24
 * Copyright © 2022 Monterosa. All rights reserved.
 *
 * More details on the license can be found at https://www.monterosa.co/sdk/license
 */
const getConnectMemoized = memoizePromise(async (host) => {
    const connect = new ConnectImpl(host);
    await connect.init();
    await connect.connect();
    return connect;
}, (host) => host);
/**
 * @internal
 */
function getConnect(host) {
    return getConnectMemoized(host);
}

/**
 * @license
 * public-types.ts
 * interact-kit
 *
 * Created by Rygor Kharytanovich <rygor@monterosa.co.uk> on 2022-03-24
 * Copyright © 2022 Monterosa. All rights reserved.
 *
 * More details on the license can be found at https://www.monterosa.co/sdk/license
 */
/**
 * Provides states of the connection health
 */
var ConnectionHealthState;
(function (ConnectionHealthState) {
    /**
     * The client is connected and the connection is healthy
     */
    ConnectionHealthState["Ok"] = "ok";
    /**
     * The client is either trying to establish a connection but failing, or
     * the client has been explicetily {@link @monterosa-sdk/connect-kit#disconnect() | disconnected}
     */
    ConnectionHealthState["Error"] = "error";
})(ConnectionHealthState || (ConnectionHealthState = {}));

/**
 * @license
 * connection_health_impl.ts
 * interact-kit
 *
 * Created by Rygor Kharytanovich <rygor@monterosa.co.uk> on 2022-02-15
 * Copyright © 2022 Monterosa. All rights reserved.
 *
 * More details on the license can be found at https://www.monterosa.co/sdk/license
 */
class ConnectionHealthImpl extends Emitter {
    constructor(connect) {
        super();
        this.connect = connect;
        this._state = ConnectionHealthState.Ok;
        this.connect.on('state', this.handleState.bind(this));
    }
    get state() {
        return this._state;
    }
    set state(state) {
        this._state = state;
        this.emit('state', state);
    }
    handleState() {
        switch (this.connect.state) {
            default:
            case State.Ok:
                this.state = ConnectionHealthState.Ok;
                break;
            case State.Error:
                this.state = ConnectionHealthState.Error;
                break;
        }
    }
}

/**
 * @license
 * index.ts
 * interact-kit
 *
 * Created by Rygor Kharytanovich <rygor@monterosa.co.uk> on 2022-02-15
 * Copyright © 2022 Monterosa. All rights reserved.
 *
 * More details on the license can be found at https://www.monterosa.co/sdk/license
 */
const getConnectionHealthMemoized = memoizePromise(async (sdk) => {
    const connect = await getConnect(sdk.options.host);
    const connectionHealth = new ConnectionHealthImpl(connect);
    return connectionHealth;
}, (sdk) => sdk.options.host);
/**
 * Returns {@link ConnectionHealth | connection health} instance
 */
function getConnectionHealth(sdk = getSdk()) {
    return getConnectionHealthMemoized(sdk);
}
/**
 * Adds an observer for when
 * {@link ConnectionHealth.state | connection health state} changed
 */
function onConnectionHealthState(connectionHealth, callback) {
    return subscribe$2(connectionHealth, 'state', callback);
}

/**
 * @license
 * internal.ts
 * interact-kit
 *
 * Created by Rygor Kharytanovich <rygor@monterosa.co.uk> on 2022-02-15
 * Copyright © 2022 Monterosa. All rights reserved.
 *
 * More details on the license can be found at https://www.monterosa.co/sdk/license
 */
async function fetchSettings(host, id) {
    const response = await fetch(`https://${host}/projects/${id.substring(0, 2)}/${id}/settings.json`);
    const data = await response.json();
    return data;
}

/**
 * @license
 * constants.ts
 * interact-kit
 *
 * Created by Rygor Kharytanovich <rygor@monterosa.co.uk> on 2022-06-02
 * Copyright © 2022 Monterosa. All rights reserved.
 *
 * More details on the license can be found at https://www.monterosa.co/sdk/license
 */
const DEFAULT_IGNORE_TIME = 30;

/**
 * @license
 * locale.ts
 * interact-kit
 *
 * Created by Rygor Kharytanovich <rygor@monterosa.co.uk> on 2022-10-18
 * Copyright © 2022 Monterosa. All rights reserved.
 *
 * More details on the license can be found at https://www.monterosa.co/sdk/license
 */
/**
 * Converts tag for identifying languages (rfc5646) into ISO language code
 */
function tagToLanguage(tag) {
    // The tag is split not only by `-` as its stated in the rfc
    // but also by `_` as it is returned by Studio for some locales
    const chunks = tag.split(/[-_]/);
    return chunks[0];
}
/**
 * Returns either:
 *   * a locale from the list of provided locales that correspond OS locale preferences
 *   * or default locale if OS locale doesn't correspond to any from the provided list
 *
 *  * @internal
 */
function getLocale(locales) {
    // The list of user's preferred language tags,
    const languageTags = [
        // where the first item is the user's default language
        navigator.language,
        // and others are the user's preferred languages
        ...navigator.languages,
    ];
    // firstly, trying to check if one of languages tag
    // has a full match with passed locales
    for (const tag of languageTags) {
        if (locales.includes(tag)) {
            // if language tag has a match return it right away
            return tag;
        }
    }
    // secondly, checking if user's language (ISO string)
    // has occurrences in the list of locales
    for (const tag of languageTags) {
        const userLang = tagToLanguage(tag);
        for (const locale of locales) {
            const studioLang = tagToLanguage(locale);
            if (studioLang === userLang) {
                // return the first locale that has the same
                // language ISO string as the user language tag
                return locale;
            }
        }
    }
    // if none locales matches users preferred languages tags
    // return default locale
    return locales[0];
}
function localise(value, locale) {
    if (Object.prototype.hasOwnProperty.call(value, locale)) {
        return value[locale];
    }
    return value.all;
}

/**
 * @license
 * project_impl.ts
 * interact-kit
 *
 * Created by Rygor Kharytanovich <rygor@monterosa.co.uk> on 2022-02-15
 * Copyright © 2022 Monterosa. All rights reserved.
 *
 * More details on the license can be found at https://www.monterosa.co/sdk/license
 */
/**
 * @internal
 */
class ProjectImpl extends Emitter {
    constructor(options, context) {
        super();
        this.id = options.id;
        this.host = options.host;
        this._context = context;
    }
    get context() {
        return this._context;
    }
    get embedUrl() {
        var _a;
        return (_a = this._listings) === null || _a === void 0 ? void 0 : _a.project.embed;
    }
    get uuid() {
        var _a;
        return (_a = this._listings) === null || _a === void 0 ? void 0 : _a.project.uuid;
    }
    get events() {
        var _a;
        return ((_a = this._listings) === null || _a === void 0 ? void 0 : _a.events) || [];
    }
    get listings() {
        return this._listings;
    }
    set listings(value) {
        let createdEvents = [];
        let deletedEvents = [];
        if (this._listings) {
            createdEvents = value.events.filter(({ id }) => !this._listings.events.some((event) => id === event.id));
        }
        if (this._listings) {
            deletedEvents = this.listings.events.filter(({ id }) => !value.events.some((event) => id === event.id));
        }
        this._listings = Object.assign({}, value);
        this._locales = this._listings.project.locales
            // clone the array first
            .map((locale) => (Object.assign({}, locale)))
            // sort an array so that the default locale is the first
            .sort((locale) => (locale.default ? -1 : 0))
            // return only keys of the locales
            .map((locale) => locale.key);
        this._locale = getLocale(this._locales);
        if (createdEvents.length > 0) {
            this.emit('listings_events_created', createdEvents);
        }
        if (deletedEvents.length > 0) {
            this.emit('listings_events_deleted', deletedEvents);
        }
        this.emit('listings');
    }
    get isLocalisationSupported() {
        var _a;
        return (_a = this._listings) === null || _a === void 0 ? void 0 : _a.project.is_localisation_supported;
    }
    get locale() {
        return this._locale;
    }
    set locale(locale) {
        if (!this.locales.includes(locale)) {
            throw new Error(`Unknown locale name "${locale}"`);
        }
        this._locale = locale;
    }
    get locales() {
        return this._locales;
    }
    get fields() {
        if (this._fields !== undefined) {
            return localise(this._fields, this.locale);
        }
        return {};
    }
    /**
     * Sets custom fields
     *
     * It accepts both Localised<Record<string, unknown>> and Record<string, unknown> to
     * avoid mistyping between setter and getter
     *
     * @internal
     */
    set fields(value) {
        const isUpdate = typeof this._fields !== 'undefined';
        this._fields = Object.assign({}, value);
        if (isUpdate) {
            this.emit('updated');
        }
    }
    get extensions() {
        var _a;
        return (_a = this._listings) === null || _a === void 0 ? void 0 : _a.assets;
    }
    get historyIgnore() {
        var _a;
        const value = (_a = this._listings) === null || _a === void 0 ? void 0 : _a.project.history_ignore;
        if (value && value > 0) {
            return value;
        }
        return DEFAULT_IGNORE_TIME;
    }
    // TODO: must be implemented
    // eslint-disable-next-line
    delete() { }
}

/**
 * @license
 * factory.ts
 * interact-kit
 *
 * Created by Rygor Kharytanovich <rygor@monterosa.co.uk> on 2022-02-15
 * Copyright © 2022 Monterosa. All rights reserved.
 *
 * More details on the license can be found at https://www.monterosa.co/sdk/license
 */
const projects = new Map();
const connects = new Map();
async function buildProject(options, context) {
    const { id, host } = options;
    if (projects.has(id)) {
        return projects.get(id);
    }
    /**
     * Create "bare" instance of the project. Until listings and settings are
     * loaded it can't be used
     */
    const project = new ProjectImpl(options, context);
    /**
     * Add it to the cache of projects, so that we could find it to populate it
     * with the data when /listings/ message is coming from the Enmasse.
     */
    projects.set(id, project);
    /**
     * Waiting when the listings and the project settings come and we would be
     * able to populate the project instance
     */
    try {
        const setup = await setupConnect(host);
        await setup.subscribeProject(id);
        /**
         * By the time goLive successfully resolved listings must be present
         * in project. If they are not, then there is no /listings/ in the channel's cache.
         * This might be due to either studio is malfunctioning or project id is incorrect.
         */
        if (!project.listings) {
            throw new Error(`Unable to fetch project ${id} listings. Possibly wrong project id.`);
        }
        project.fields = await fetchSettings(project.host, project.id);
        return project;
    }
    catch (err) {
        /**
         * If anything goes wrong we need to unsubscribe from the Enmasse project's
         * channel by switching it to the offline mode.
         */
        if (connects.has(id)) {
            const connect = connects.get(id);
            connect.unsubscribeProject(id);
        }
        /**
         * And project should be removed from the list of cached projects so that
         * it won't be automatically returned upon consequent buildProject call
         */
        projects.delete(id);
        throw err;
    }
}
async function setupConnect(host) {
    if (connects.has(host)) {
        return connects.get(host);
    }
    const connect = await getConnect(host);
    connect.on('message', handleConnectMessage$1);
    return connect;
}
async function handleConnectMessage$1(message) {
    const { id, klass, data } = message;
    const project = projects.get(id);
    if (!project) {
        return;
    }
    switch (klass) {
        case Klass.Listings:
            await handleListings(project, data[0]);
            break;
    }
}
async function handleListings(project, data) {
    var _a;
    const listings = JSON.parse(data);
    const newDigest = listings.project.app_settings_digest;
    const oldDigest = (_a = project.listings) === null || _a === void 0 ? void 0 : _a.project.app_settings_digest;
    project.listings = listings;
    try {
        if (oldDigest !== undefined && newDigest !== oldDigest) {
            project.fields = await fetchSettings(project.host, project.id);
        }
    }
    catch (_b) {
        console.warn(`Unable to fetch project ${project.id} settings`);
    }
}

/**
 * @license
 * api.ts
 * interact-kit
 *
 * Created by Rygor Kharytanovich <rygor@monterosa.co.uk> on 2022-02-15
 * Copyright © 2022 Monterosa. All rights reserved.
 *
 * More details on the license can be found at https://www.monterosa.co/sdk/license
 */
/**
 * @internal
 */
const getProjectMemoized = memoizePromise(async (sdk) => {
    const project = await buildProject({
        id: sdk.options.projectId,
        host: sdk.options.host,
    }, {
        sdk,
    });
    return project;
}, (sdk) => sdk.options.projectId);
/**
 * Returns {@link InteractProject | project instance} associated
 * with the {@link @monterosa-sdk/core#MonterosaSdk | configured sdk}
 */
function getProject(sdk = getSdk()) {
    return getProjectMemoized(sdk);
}
/**
 * @internal
 */
async function fetchListings(host, projectId) {
    const response = await fetch(`https://${host}/projects/${projectId.substring(0, 2)}/${projectId}/listings.json`);
    const data = (await response.json());
    return data;
}
/**
 * Adds an observer for when {@link InteractProject.fields |  project fields}
 * are updated
 */
function onProjectFieldsUpdated(project, callback) {
    return subscribe$2(project, 'updated', callback);
}
/**
 * @internal
 */
function onProjectListingsUpdated(project, callback) {
    return subscribe$2(project, 'listings', callback);
}

/*! *****************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */

function __values(o) {
    var s = typeof Symbol === "function" && Symbol.iterator, m = s && o[s], i = 0;
    if (m) return m.call(o);
    if (o && typeof o.length === "number") return {
        next: function () {
            if (o && i >= o.length) o = void 0;
            return { value: o && o[i++], done: !o };
        }
    };
    throw new TypeError(s ? "Object is not iterable." : "Symbol.iterator is not defined.");
}

function __asyncValues(o) {
    if (!Symbol.asyncIterator) throw new TypeError("Symbol.asyncIterator is not defined.");
    var m = o[Symbol.asyncIterator], i;
    return m ? m.call(o) : (o = typeof __values === "function" ? __values(o) : o[Symbol.iterator](), i = {}, verb("next"), verb("throw"), verb("return"), i[Symbol.asyncIterator] = function () { return this; }, i);
    function verb(n) { i[n] = o[n] && function (v) { return new Promise(function (resolve, reject) { v = o[n](v), settle(resolve, reject, v.done, v.value); }); }; }
    function settle(resolve, reject, d, v) { Promise.resolve(v).then(function(v) { resolve({ value: v, done: d }); }, reject); }
}

/**
 * @license
 * internal.ts
 * interact-kit
 *
 * Created by Rygor Kharytanovich <rygor@monterosa.co.uk> on 2022-03-24
 * Copyright © 2022 Monterosa. All rights reserved.
 *
 * More details on the license can be found at https://www.monterosa.co/sdk/license
 */
async function fetchHistory(host, id) {
    const response = await fetch(`https://${host}/events/${id.substring(0, 2)}/${id}/history.json`);
    if (!response.ok) {
        throw new Error(`Failed to fetch event ${id} history`);
    }
    const data = await response.json();
    return data;
}

/**
 * @license
 * public-types.ts
 * interact-kit
 *
 * Created by Rygor Kharytanovich <rygor@monterosa.co.uk> on 2022-03-24
 * Copyright © 2022 Monterosa. All rights reserved.
 *
 * More details on the license can be found at https://www.monterosa.co/sdk/license
 */
/**
 * Describes the event state.
 */
var EventState;
(function (EventState) {
    /**
     * The event is in the `upcoming` state when its {@link InteractEvent.startAt | start time}
     * less than the {@link now | current time}.
     */
    EventState["Upcoming"] = "upcoming";
    /**
     * The event is in the `active` state when its {@link InteractEvent.startAt | start time}
     * equal or more than the {@link now | current time} and less than the
     * {@link InteractEvent.endAt | end time}.
     */
    EventState["Active"] = "active";
    /**
     * @internal
     */
    EventState["Prolonged"] = "prolonged";
    /**
     * The event is in the `finished` state when its {@link InteractEvent.endAt | end time}
     * equal or more than the {@link now | current time}.
     */
    EventState["Finished"] = "finished";
})(EventState || (EventState = {}));
var InitState;
(function (InitState) {
    InitState["Uninitialised"] = "uninitialised";
    InitState["Initialising"] = "initialising";
    InitState["Initialised"] = "initialised";
})(InitState || (InitState = {}));

/**
 * @license
 * event_impl.ts
 * interact-kit
 *
 * Created by Rygor Kharytanovich <rygor@monterosa.co.uk> on 2022-02-15
 * Copyright © 2022 Monterosa. All rights reserved.
 *
 * More details on the license can be found at https://www.monterosa.co/sdk/license
 */
/**
 * @internal
 */
class EventImpl extends Emitter {
    constructor(data, context) {
        super();
        this._initState = InitState.Uninitialised;
        this._history = null;
        this._data = data;
        this._context = context;
        this._state = this.calculateState();
        this._internalState = this.calculateInternalState();
        this.unsubscribeStateHandler = onTick(() => this.handleState());
        this.unsubscribeInternalStateHandler = onTick(() => {
            this.handleInternalState();
        });
        this.unsubscribeListingsHandler = onProjectListingsUpdated(context.project, () => this.handleListings());
    }
    calculateState() {
        if (!this._data.started || now() < this.startAt) {
            return EventState.Upcoming;
        }
        if (now() < this.endAt) {
            return EventState.Active;
        }
        return EventState.Finished;
    }
    calculateInternalState() {
        if (!this._data.started || now() < this.startAt) {
            return EventState.Upcoming;
        }
        if (now() < this.endAt) {
            return EventState.Active;
        }
        if (now() < this.endAt + this._data.extra_time) {
            return EventState.Prolonged;
        }
        return EventState.Finished;
    }
    handleState() {
        const state = this.calculateState();
        if (state === EventState.Finished) {
            this.unsubscribeStateHandler();
        }
        if (state !== this._state) {
            this._state = state;
            this.emit('state', state);
        }
    }
    handleInternalState() {
        const internalState = this.calculateInternalState();
        if (internalState === EventState.Finished) {
            this.unsubscribeInternalStateHandler();
        }
        if (internalState !== this._internalState) {
            this._internalState = internalState;
            this.emit('internal_state', internalState);
        }
    }
    handleListings() {
        const { project } = this.context;
        const data = project.events.find(({ id }) => id === this.id);
        if (data && this._data.digest !== data.digest) {
            this._data = data;
            this.emit('updated');
        }
    }
    /** @internal */
    destroy() {
        this.unsubscribeStateHandler();
        this.unsubscribeInternalStateHandler();
        this.unsubscribeListingsHandler();
    }
    get context() {
        return this._context;
    }
    get id() {
        return this._data.id;
    }
    get name() {
        return this._data.name;
    }
    get startAt() {
        return this._data.start_at;
    }
    get startAtIso() {
        return this._data.start_at_iso;
    }
    get endAt() {
        return this._data.end_at;
    }
    get endAtIso() {
        return this._data.end_at_iso;
    }
    get duration() {
        return this._data.duration;
    }
    get fields() {
        const { locale } = this.context.project;
        return localise(this._data.custom_fields, locale);
    }
    get state() {
        return this._state;
    }
    get internalState() {
        return this._internalState;
    }
    get initState() {
        return this._initState;
    }
    set initState(state) {
        this._initState = state;
    }
    get history() {
        return this._history;
    }
    set history(value) {
        this._history = value;
        this.emit('history', value);
    }
    get canSubscribe() {
        return (this._internalState === EventState.Active ||
            this._internalState === EventState.Prolonged);
    }
    get canLoadHistory() {
        return now() >= this.startAt + this.context.project.historyIgnore;
    }
}

/**
 * @license
 * factory.ts
 * interact-kit
 *
 * Created by Rygor Kharytanovich <rygor@monterosa.co.uk> on 2022-02-22
 * Copyright © 2022 Monterosa. All rights reserved.
 *
 * More details on the license can be found at https://www.monterosa.co/sdk/license
 */
const events = new Map();
const healthListeners = new Map();
function buildEvent(options, context) {
    if (events.has(options.id)) {
        return events.get(options.id);
    }
    const event = new EventImpl(options, context);
    event.on('internal_state', handleEventInternalState.bind(event));
    events.set(options.id, event);
    return event;
}
async function init(event) {
    if (event.initState === InitState.Initialising) {
        return Promise.reject(new Error("can't initialise event, event is already being initialised"));
    }
    if (event.initState === InitState.Initialised) {
        return Promise.reject(new Error("can't initialise event, event is already initialised"));
    }
    event.initState = InitState.Initialising;
    if (event.canLoadHistory && event.history === null) {
        const history = await fetchHistory(event.context.project.host, event.id);
        event.history = history;
    }
    if (event.canSubscribe) {
        await subscribe(event);
    }
    await listenConnHealth(event);
    event.initState = InitState.Initialised;
    return Promise.resolve();
}
async function subscribe(event) {
    if (!event.canSubscribe) {
        return Promise.reject(new Error("Event can't be subscribed"));
    }
    const connect = await getConnect(event.context.project.host);
    await connect.subscribeEvent(event.id);
    return Promise.resolve();
}
async function unsubscribe(event) {
    const connect = await getConnect(event.context.project.host);
    await connect.unsubscribeEvent(event.id);
    return Promise.resolve();
}
async function listenConnHealth(event) {
    const { project } = event.context;
    if (healthListeners.has(project.id)) {
        return;
    }
    const connect = await getConnect(project.host);
    if (connect.enmasse !== null) {
        const unsub = onConnected(connect.enmasse, handleEnmasseConnected);
        healthListeners.set(project.id, unsub);
    }
}
async function handleEventInternalState(state) {
    if (state === EventState.Finished) {
        await unsubscribe(this);
    }
}
async function handleEnmasseConnected() {
    var e_1, _a;
    try {
        for (var _b = __asyncValues(Array.from(events.values())), _c; _c = await _b.next(), !_c.done;) {
            const event = _c.value;
            if (event.initState === InitState.Initialised) {
                const history = await fetchHistory(event.context.project.host, event.id);
                event.history = history;
            }
        }
    }
    catch (e_1_1) { e_1 = { error: e_1_1 }; }
    finally {
        try {
            if (_c && !_c.done && (_a = _b.return)) await _a.call(_b);
        }
        finally { if (e_1) throw e_1.error; }
    }
}

/**
 * @internal
 */
const getEventsMemoized = memoizePromise(async (project) => {
    if (project === undefined) {
        project = await getProject();
    }
    const context = Object.assign(Object.assign({}, project.context), { project });
    return (project.events
        // Calling builder function that creates an event instance.
        // Existing event will be returned if it already exists in the cache
        .map((data) => buildEvent(data, context))
        // Sort events so the most recent event is first, and the oldest is last
        .sort((a, b) => a.startAt - b.startAt));
}, (project) => {
    if (project === undefined) {
        const sdk = getSdk();
        return sdk.options.projectId;
    }
    return project.id;
});
/**
 * Returns all events in a project, including all active events and
 * past/upcoming events, according to Listings settings in Project Setup
 * in Studio.
 *
 * @param project - A project instance. If not provided,
 *   the one from the default sdk will be fetched.
 */
function getEvents(project) {
    return getEventsMemoized(project);
}
/**
 * @internal
 */
const getEventMemoized = memoizePromise(async (id, project) => {
    if (project === undefined) {
        project = await getProject();
    }
    if (events.has(id)) {
        return events.get(id);
    }
    const context = Object.assign(Object.assign({}, project.context), { project });
    const data = project.events.find((eventData) => eventData.id === id);
    if (data !== undefined) {
        const event = buildEvent(data, context);
        if (event.canLoadHistory) {
            const history = await fetchHistory(project.host, id);
            event.history = history;
        }
        return event;
    }
    try {
        const history = await fetchHistory(project.host, id);
        const event = buildEvent(history.config, context);
        event.history = history;
        return event;
    }
    catch (err) {
        return null;
    }
}, (id) => id);
/**
 * Returns an event by its id
 *
 * @param id - Id of the event
 * @param project - A project instance. If not provided,
 *   the one from the default sdk will be fetched.
 *
 * @returns A an event associated with the provided id and project or null if no such
 *   event exists in the project.
 */
function getEvent(id, project) {
    return getEventMemoized(id, project);
}
/**
 * Adds an observer for when {@link InteractEvent.state | event state} changed
 */
function onEventState(event, callback) {
    return subscribe$2(event, 'state', callback);
}
/**
 * Adds an observer for when event's data changed
 */
function onEventUpdated(event, callback) {
    return subscribe$2(event, 'updated', callback);
}
/**
 * Adds an observer that is called when an event is added to listings.
 *
 * @remarks
 * The following actions will result in adding an event to listings:
 *
 * - The start of the event in Studio
 *
 * - The scheduling of a future event in Studio, when listings are configured
 *   to include future events in Project Settings.
 *
 * - When a future event starts, a new future event may be added to the list,
 *   depending on the project configuration, to ensure a minimum number of
 *   upcoming events are always available.
 */
function onEventAdded(project, callback) {
    return subscribe$2(project, 'listings_events_created', async (data) => {
        var e_1, _a;
        try {
            for (var data_1 = __asyncValues(data), data_1_1; data_1_1 = await data_1.next(), !data_1_1.done;) {
                const options = data_1_1.value;
                const event = await getEvent(options.id, project);
                if (event !== null) {
                    callback(event);
                }
            }
        }
        catch (e_1_1) { e_1 = { error: e_1_1 }; }
        finally {
            try {
                if (data_1_1 && !data_1_1.done && (_a = data_1.return)) await _a.call(data_1);
            }
            finally { if (e_1) throw e_1.error; }
        }
    });
}
/**
 * {@link onEventAdded} alias.
 *
 * @deprecated Use the new {@link onEventAdded} function instead.
 */
function onEventPublished(project, callback) {
    console.warn('onEventPublished() is deprecated. Please use onEventAdded() instead');
    return onEventAdded(project, callback);
}
/**
 * Adds an observer that is called when an event is removed from listings.
 *
 * @remarks
 * The following actions will result in removing an event from listings:
 *
 * - The deletion of the event from Studio
 *
 * - The change of listings confuguration in Project Settings to exclude future
 *   or past events.
 *
 * - The event is removed after a period of time since its completion. This period
 *   is calculated as 45 seconds plus the Maximum allowed delay (configured in
 *   Project Settings in Studio).
 */
function onEventRemoved(project, callback) {
    return subscribe$2(project, 'listings_events_deleted', async (data) => {
        var e_2, _a;
        try {
            for (var data_2 = __asyncValues(data), data_2_1; data_2_1 = await data_2.next(), !data_2_1.done;) {
                const options = data_2_1.value;
                const event = await getEvent(options.id, project);
                if (event !== null) {
                    callback(event);
                }
            }
        }
        catch (e_2_1) { e_2 = { error: e_2_1 }; }
        finally {
            try {
                if (data_2_1 && !data_2_1.done && (_a = data_2.return)) await _a.call(data_2);
            }
            finally { if (e_2) throw e_2.error; }
        }
    });
}

/**
 * @license
 * public-types.ts
 * interact-kit
 *
 * Created by Rygor Kharytanovich <rygor@monterosa.co.uk> on 2022-03-24
 * Copyright © 2022 Monterosa. All rights reserved.
 *
 * More details on the license can be found at https://www.monterosa.co/sdk/license
 */
/**
 * A map of potential error codes to compare with errors thrown by
 * vote() or validateAnswer() functions.
 */
var AnswerError;
(function (AnswerError) {
    AnswerError["OptionIndexOutOfRange"] = "out_of_range";
    AnswerError["BelowMinVoteOptions"] = "below_min_vote_options";
    AnswerError["AboveMaxVoteOptions"] = "above_max_vote_options";
    AnswerError["AboveMaxVotesPerUser"] = "above_max_per_user";
    AnswerError["AboveMaxVotesPerOption"] = "above_max_per_option";
    AnswerError["VotedOnNonInteractiveElement"] = "non_interactive_element";
    AnswerError["VotedOnClosedElement"] = "closed_element";
    AnswerError["EmptyVote"] = "empty_vote";
})(AnswerError || (AnswerError = {}));
const AnswerErrorMessages = {
    [AnswerError.OptionIndexOutOfRange]: (max, idx) => `Options index ${idx} is out of range [0..${max}]`,
    [AnswerError.BelowMinVoteOptions]: (min, total) => `Below min (${min}) vote options: ${total}`,
    [AnswerError.AboveMaxVoteOptions]: (max, total) => `Above max (${max}) vote options: ${total}`,
    [AnswerError.AboveMaxVotesPerUser]: (max, total) => `Above max (${max}) votes per user: ${total}`,
    [AnswerError.AboveMaxVotesPerOption]: (max, total) => `Above max (${max}) votes per option: ${total}`,
    [AnswerError.VotedOnNonInteractiveElement]: (id) => `Non interact element ${id}`,
    [AnswerError.VotedOnClosedElement]: (id) => `Element ${id} is closed`,
    [AnswerError.EmptyVote]: (indices) => `Empty value for options indices: ${indices}`,
};
/**
 * The ElementState represents the potential states an element is in.
 */
var ElementState;
(function (ElementState) {
    /**
     * Element is opened and active. The user can vote if
     * element is `interactive`
     */
    ElementState["Opened"] = "opened";
    /**
     * Element is closed and the user can no longer vote for
     * `interactive` elements
     */
    ElementState["Closed"] = "closed";
})(ElementState || (ElementState = {}));

/**
 * @license
 * answer.ts
 * interact-kit
 *
 * Created by Rygor Kharytanovich <rygor@monterosa.co.uk> on 2022-04-13
 * Copyright © 2022 Monterosa. All rights reserved.
 *
 * More details on the license can be found at https://www.monterosa.co/sdk/license
 */
/**
 * An `Answer` contains data of the user answer. Can be used to manage number of
 * votes that the user placed for a specific option.
 */
class Answer {
    constructor(answers = []) {
        this._answers = new Map();
        for (const { option, value } of answers) {
            this._answers.set(option, value);
        }
    }
    /**
     * The array of user answers
     */
    get answers() {
        return Array.from(this._answers.entries()).map(([option, value]) => ({
            option,
            value,
        }));
    }
    /**
     * Instantiate Answer class with the provided options indices. Number
     * of votes will be set to 1 for all options
     *
     * @example
     * ```javascript
     * // user votes for the first and fourth options
     * const answer = Answer.fromIndices([0, 3]);
     * ```
     *
     * @param indices - The array of option indices
     */
    static fromIndices(indices) {
        return new Answer(indices.map((option) => ({
            option,
            value: 1,
        })));
    }
    /**
     * Adds a vote for the provided option index
     *
     * @param option - The index of the option, starting with 0
     * @param value - The number of votes that will be placed against the option
     */
    set(option, value) {
        this._answers.set(option, value);
        return this;
    }
    /**
     * Removes the vote for the provided option index
     *
     * @param option - The index of the option, starting with 0
     */
    remove(option) {
        this._answers.delete(option);
        return this;
    }
    /** @internal */
    toJSON() {
        return this.answers;
    }
    /** @internal */
    static fromJSON(value) {
        return new Answer(JSON.parse(value));
    }
}

/**
 * @license
 * types.ts
 * interact-kit
 *
 * Created by Rygor Kharytanovich <rygor@monterosa.co.uk> on 2022-03-24
 * Copyright © 2022 Monterosa. All rights reserved.
 *
 * More details on the license can be found at https://www.monterosa.co/sdk/license
 */
/**
 * Describes element types
 */
var ElementType;
(function (ElementType) {
    /**
     * Data element
     */
    ElementType["Data"] = "data";
    /**
     * Poll element
     */
    ElementType["Poll"] = "poll";
    /**
     * Regular poll element
     */
    ElementType["RegularPoll"] = "rpoll";
    /**
     * Diametric poll element
     */
    ElementType["DiametricPoll"] = "dpoll";
    /**
     * Emoting poll
     */
    ElementType["EmotingPoll"] = "emo";
    /**
     * Powerbar element
     */
    ElementType["Powerbar"] = "powerbar";
    /**
     * Prediction element
     */
    ElementType["Prediction"] = "prediction";
    /**
     * Trivia element
     */
    ElementType["Trivia"] = "trivia";
})(ElementType || (ElementType = {}));
var ShowResultsMode;
(function (ShowResultsMode) {
    ShowResultsMode["OnVote"] = "vote";
    ShowResultsMode["OnClose"] = "close";
    ShowResultsMode["OnEventEnd"] = "event_end";
    ShowResultsMode["Manual"] = "manual";
    ShowResultsMode["Never"] = "never";
    ShowResultsMode["Always"] = "always";
})(ShowResultsMode || (ShowResultsMode = {}));

/**
 * @license
 * element_impl.ts
 * interact-kit
 *
 * Created by Rygor Kharytanovich <rygor@monterosa.co.uk> on 2022-03-24
 * Copyright © 2022 Monterosa. All rights reserved.
 *
 * More details on the license can be found at https://www.monterosa.co/sdk/license
 */
/**
 * @internal
 */
class ElementImpl extends Emitter {
    constructor(data, context) {
        var _a, _b;
        super();
        this._results = null;
        this._userAnswer = null;
        this._correctOption = null;
        this._data = data;
        this._context = context;
        this._results = ((_a = data.data) === null || _a === void 0 ? void 0 : _a.results) || null;
        this._correctOption = ((_b = data.data) === null || _b === void 0 ? void 0 : _b.correct_option)
            ? data.data.correct_option - 1
            : null;
        this._state = this.calculateState();
        this.unsubscribeStateHandler = onTick(() => this.handleState());
    }
    calculateState() {
        if (now() < this.publishedAt + this.duration) {
            return ElementState.Opened;
        }
        return ElementState.Closed;
    }
    handleState() {
        const state = this.calculateState();
        if (state === ElementState.Closed) {
            this.unsubscribeStateHandler();
        }
        if (state !== this._state) {
            this._state = state;
            this.emit('state', state);
        }
    }
    update(data) {
        this._data = data;
    }
    destroy() {
        this.unsubscribeStateHandler();
    }
    get context() {
        return this._context;
    }
    get id() {
        return this._data.id;
    }
    get eventId() {
        return this.context.event.id;
    }
    get state() {
        return this._state;
    }
    get type() {
        return this._data.type;
    }
    get contentType() {
        return this._data.content_type;
    }
    get publishedAt() {
        return this._data.published_at;
    }
    get publishedAtIso() {
        return this._data.published_at_iso;
    }
    get updatedAt() {
        return this._data.updated_at;
    }
    get updatedAtIso() {
        return this._data.updated_at_iso;
    }
    get duration() {
        return this._data.duration;
    }
    get fields() {
        const { locale } = this.context.project;
        return localise(this._data.custom_fields, locale);
    }
    get interactive() {
        return this.type !== ElementType.Data;
    }
    get pollId() {
        return this._data.data.id || null;
    }
    get question() {
        const { locale } = this.context.project;
        if (this._data.data.question === undefined) {
            return null;
        }
        return localise(this._data.data.question, locale);
    }
    get answerOptions() {
        const { locale } = this.context.project;
        if (this._data.data.options === undefined) {
            return null;
        }
        return localise(this._data.data.options, locale);
    }
    get minOptionsPerVote() {
        return this._data.data.min_options_per_vote || 1;
    }
    get maxOptionsPerVote() {
        return this._data.data.max_options_per_vote || 1;
    }
    get maxVotesPerOption() {
        return this._data.data.max_votes_per_option || 1;
    }
    get maxVotesPerUser() {
        return this._data.data.max_votes_per_user || null;
    }
    get userAnswer() {
        return this._userAnswer;
    }
    set userAnswer(value) {
        this._userAnswer = value;
    }
    get hasBeenAnswered() {
        return this._userAnswer !== null;
    }
    get showResultsMode() {
        return this._data.data.reveal_results_mode || null;
    }
    get canShowResults() {
        switch (this.showResultsMode) {
            case ShowResultsMode.OnVote:
                return this.hasBeenAnswered || this.state === ElementState.Closed;
            case ShowResultsMode.OnClose:
                return this.state === ElementState.Closed;
            case ShowResultsMode.Manual:
                return this._results !== null;
            case ShowResultsMode.OnEventEnd:
                return this.context.event.state === EventState.Finished;
            case ShowResultsMode.Never:
                return false;
            case ShowResultsMode.Always:
            default:
                return true;
        }
    }
    get results() {
        if (!this.canShowResults) {
            return null;
        }
        return this._results;
    }
    set results(results) {
        // skip setting results if they weren't changed
        if (JSON.stringify(results) === JSON.stringify(this.results)) {
            return;
        }
        this._results = results;
        if (this.canShowResults) {
            this.emit('results');
        }
    }
    get correctOption() {
        return this._correctOption;
    }
    set correctOption(value) {
        this._correctOption = value;
    }
    get isCorrectOptionRevealed() {
        return this._correctOption !== null;
    }
}

/**
 * @license
 * calculate_percentage.ts
 * interact-kit
 *
 * Created by Rygor Kharytanovich <rygor@monterosa.co.uk> on 2022-04-07
 * Copyright © 2022 Monterosa. All rights reserved.
 *
 * More details on the license can be found at https://www.monterosa.co/sdk/license
 */
const calculatePercentage = (values) => {
    // Calculate sum of all votes
    const sum = values.reduce((memo, value) => memo + value, 0);
    // create array of hashes
    const results = values.map((value, idx) => ({
        idx,
        votes: value,
        percentage: Math.round((100 * value) / sum) || 0,
    }));
    // Sum them all up - this can be less, equal or greater than a 100%
    let total = results.reduce((memo, { percentage }) => memo + percentage, 0);
    total = total || 100;
    // Calculate number of percent that we are missing or that we need to lose
    let delta = 100 - total;
    const sign = delta >= 0 ? 1 : -1;
    // Order all options by number of votes
    results.sort((a, b) => {
        const result = sign * (b.votes - a.votes);
        if (result !== 0) {
            return result;
        }
        return sign * (a.idx - b.idx);
    });
    // If we have percents to lose then take one percent off lowest options
    // If we are missing percents then add one percent to highest options
    let i = 0;
    while (delta !== 0) {
        if (results[i].votes > 0) {
            results[i].percentage += sign;
            delta -= sign;
        }
        i++;
    }
    // Restore options order
    results.sort((a, b) => a.idx - b.idx);
    return results.map((item) => item.percentage);
};

/**
 * @license
 * factory.ts
 * interact-kit
 *
 * Created by Rygor Kharytanovich <rygor@monterosa.co.uk> on 2022-03-24
 * Copyright © 2022 Monterosa. All rights reserved.
 *
 * More details on the license can be found at https://www.monterosa.co/sdk/license
 */
const elements = new Map();
const storageAvailable = checkAvailability();
function buildElement(options, context) {
    if (elements.has(options.id)) {
        return elements.get(options.id);
    }
    const element = new ElementImpl(options, context);
    restoreAnswer(element);
    elements.set(element.id, element);
    return element;
}
function getElementByPolld(pollId) {
    return Array.from(elements.values()).find((element) => element.pollId === pollId);
}
const configureEventMemoized = memoizePromise(async (event) => {
    event.on('history', handleEventHistory);
    if (event.history !== null) {
        handleEventHistory(event.history);
    }
    await configureConnect(event.context.sdk.options.host);
    await init(event);
}, (event) => event.id);
async function configureEvent(event) {
    return configureEventMemoized(event);
}
const configureConnectMemoized = memoizePromise(async (host) => {
    const connect = await getConnect(host);
    connect.on('message', handleConnectMessage);
}, (host) => host);
async function configureConnect(host) {
    return configureConnectMemoized(host);
}
async function handleConnectMessage(message) {
    const { klass } = message;
    switch (klass) {
        case Klass.Create:
            await handleCreateMessage(message);
            break;
        case Klass.Revoke:
            await handleRevokeMessage(message);
            break;
        case Klass.Feedback:
            await handleFeedbackMessage(message);
            break;
        case Klass.Reveal:
            await handleRevealMessage(message);
            break;
    }
}
async function handleCreateMessage(message) {
    const { id, data } = message;
    const options = JSON.parse(data[0]);
    const event = await getEvent(id);
    if (event === null) {
        return;
    }
    if (elements.has(options.id)) {
        const element = elements.get(options.id);
        if (options.updated_at > element.updatedAt) {
            element.update(options);
            event.emit('update', element);
        }
    }
    else {
        const context = Object.assign(Object.assign({}, event.context), { event });
        const element = buildElement(options, context);
        event.emit('publish', element);
    }
}
function handleRevokeMessage(message) {
    const { data } = message;
    const { id } = JSON.parse(data[0]);
    if (!elements.has(id)) {
        return;
    }
    const element = elements.get(id);
    elements.delete(id);
    element.destroy();
    element.context.event.emit('revoke', element);
}
async function handleFeedbackMessage(message) {
    const { data } = message;
    // Feedback message format:
    //
    // [pollId, isFinal, sources, counters, timestamp]
    const pollId = +data[0];
    const counters = data[3];
    const element = getElementByPolld(pollId);
    if (!element) {
        return;
    }
    const votes = counters.split(':').map((value) => +value);
    const percentages = calculatePercentage(votes);
    const results = votes.map((item, idx) => ({
        percentage: percentages[idx],
        votes: item,
    }));
    element.results = results;
}
function handleRevealMessage(message) {
    const { data } = message;
    // Reveal message format:
    //
    // [pollId, index (1-based), timestamp]
    const pollId = +data[0];
    const correctOption = +data[1] - 1;
    const element = getElementByPolld(pollId);
    if (!element) {
        return;
    }
    element.correctOption = correctOption;
    element.context.event.emit('update', element);
}
async function handleEventHistory(history) {
    if (history.timeline.length === 0) {
        return;
    }
    const event = await getEvent(history.config.id);
    if (event === null) {
        return;
    }
    const context = Object.assign(Object.assign({}, event.context), { event });
    for (const snapshot of history.timeline) {
        if (!elements.has(snapshot.id)) {
            buildElement(snapshot, context);
        }
    }
}
async function sendAnswer(element, userAnswer) {
    const { project, event } = element.context;
    const connect = await getConnect(project.host);
    const answer = userAnswer.answers
        .map(({ option, value }) => [option + 1, value])
        .flat()
        .join(':');
    connect.sendVote(event.id, element.pollId, answer);
}
function storeAnswer(element, userAnswer) {
    try {
        element.userAnswer = userAnswer;
        if (storageAvailable) {
            // Can throw a "QuotaExceededError" DOMException exception
            // if the quota has been exceeded.
            setItem(`element_${element.id}_vote`, JSON.stringify(userAnswer));
        }
    }
    catch (err) {
        if (err instanceof Error) {
            console.warn(`Can't store user answer: ${err.message}`);
        }
    }
}
function restoreAnswer(element) {
    if (storageAvailable) {
        const userAnswer = getItem(`element_${element.id}_vote`);
        if (userAnswer !== null) {
            element.userAnswer = Answer.fromJSON(userAnswer);
        }
    }
}

/**
 * @license
 * api.ts
 * interact-kit
 *
 * Created by Rygor Kharytanovich <rygor@monterosa.co.uk> on 2022-03-24
 * Copyright © 2022 Monterosa. All rights reserved.
 *
 * More details on the license can be found at https://www.monterosa.co/sdk/license
 */
/**
 * @internal
 */
const getElementMemoized = memoizePromise(async (event, id) => {
    await configureEvent(event);
    const element = Array.from(elements.values()).find((el) => el.id === id);
    return element || null;
}, (event, id) => id);
/**
 * Returns an element of a specific event by its id
 */
function getElement(event, id) {
    return getElementMemoized(event, id);
}
/**
 * @internal
 */
const getElementsMemoized = memoizePromise(async (event) => {
    await configureEvent(event);
    return (Array.from(elements.values())
        // Get elements which belong only to the provided event
        .filter((element) => element.eventId === event.id)
        // Sort elements so the most recent event is first, and the oldest is last
        .sort((a, b) => a.publishedAt - b.publishedAt));
}, (event) => event.id, {
    clearOnResolve: true,
});
/**
 * Returns the list of elements published in a specific event
 */
function getElements(event) {
    return getElementsMemoized(event);
}
function answer(element, ...value) {
    const answerObj = answerParametersToAnswer(...value);
    validateAnswer(element, ...value);
    sendAnswer(element, answerObj);
    storeAnswer(element, answerObj);
}
function validateAnswer(element, ...value) {
    const answerObj = answerParametersToAnswer(...value);
    const { answers } = answerObj;
    const indices = answers.map(({ option }) => option);
    const values = answers.map(({ value: v }) => v);
    const maxIndex = Math.max(...indices);
    const maxValue = Math.max(...values);
    const sumValues = values.reduce((memo, val) => memo + val, 0);
    const emptyIndices = values.reduce((memo, val, idx) => {
        if (val === 0) {
            memo.push(indices[idx]);
        }
        return memo;
    }, []);
    if (!element.interactive) {
        throw createError(AnswerError.VotedOnNonInteractiveElement, AnswerErrorMessages, element.id);
    }
    if (element.state === ElementState.Closed) {
        throw createError(AnswerError.VotedOnClosedElement, AnswerErrorMessages, element.id);
    }
    if (maxIndex > element.answerOptions.length - 1) {
        throw createError(AnswerError.OptionIndexOutOfRange, AnswerErrorMessages, element.answerOptions.length - 1, maxIndex);
    }
    if (element.minOptionsPerVote !== null &&
        indices.length < element.minOptionsPerVote) {
        throw createError(AnswerError.BelowMinVoteOptions, AnswerErrorMessages, element.minOptionsPerVote, indices.length);
    }
    if (element.maxOptionsPerVote !== null &&
        indices.length > element.maxOptionsPerVote) {
        throw createError(AnswerError.AboveMaxVoteOptions, AnswerErrorMessages, element.maxOptionsPerVote, indices.length);
    }
    if (sumValues > element.maxVotesPerUser) {
        throw createError(AnswerError.AboveMaxVotesPerUser, AnswerErrorMessages, element.maxVotesPerUser, sumValues);
    }
    if (maxValue > element.maxVotesPerOption) {
        throw createError(AnswerError.AboveMaxVotesPerOption, AnswerErrorMessages, element.maxVotesPerOption, maxValue);
    }
    if (emptyIndices.length > 0) {
        throw createError(AnswerError.EmptyVote, AnswerErrorMessages, emptyIndices.join(', '));
    }
}
function answerParametersToAnswer(...value) {
    const answerValues = value.flat();
    if (isAnswer(answerValues[0])) {
        return answerValues[0];
    }
    if (isNumbers(answerValues)) {
        return Answer.fromIndices(answerValues);
    }
    if (isAnswerValues(answerValues)) {
        return new Answer(answerValues);
    }
    return new Answer();
}
function isAnswer(value) {
    return value instanceof Answer;
}
function isAnswerValues(value) {
    return value.every((v) => Object.prototype.hasOwnProperty.call(v, 'option') &&
        Object.prototype.hasOwnProperty.call(v, 'value'));
}
function isNumbers(value) {
    return value.every((v) => typeof v === 'number');
}
/**
 * Adds an observer for when {@link InteractElement.results | element results}
 * are updated
 */
function onElementResults(element, callback) {
    return subscribe$2(element, 'results', callback);
}
/**
 * Adds an observer for when {@link InteractElement.state | element state}
 * changed
 */
function onElementStateChanged(element, callback) {
    return subscribe$2(element, 'state', callback);
}
/**
 * Adds an observer for when {@link InteractElement.fields | element's data}
 * is updated
 */
function onElementUpdated(event, callback) {
    configureEvent(event);
    return subscribe$2(event, 'update', callback);
}
/**
 * Adds an observer for when a new element is published
 */
function onElementPublished(event, callback) {
    configureEvent(event);
    return subscribe$2(event, 'publish', (element) => {
        if (event.initState === InitState.Initialised) {
            callback(element);
        }
    });
}
/**
 * Adds an observer for when an element is revoked
 */
function onElementRevoked(event, callback) {
    configureEvent(event);
    return subscribe$2(event, 'revoke', callback);
}

export { Answer, AnswerError, Channel, ConnectionHealthState, ElementImpl, ElementState, ElementType, EventImpl, EventState, Klass, ProjectImpl, State, answer, fetchListings, getConnect, getConnectionHealth, getElement, getElementMemoized, getElements, getElementsMemoized, getEvent, getEventMemoized, getEvents, getEventsMemoized, getProject, getProjectMemoized, login, onConnectionHealthState, onElementPublished, onElementResults, onElementRevoked, onElementStateChanged, onElementUpdated, onEventAdded, onEventPublished, onEventRemoved, onEventState, onEventUpdated, onProjectFieldsUpdated, onProjectListingsUpdated, validateAnswer };
//# sourceMappingURL=index.esm2017.js.map
